import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Modal,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  jids,
  yesNoOptions,
} from '../../../data/forms/generalDropdownOptions.js';
import {
  billAcceptorNumberItems,
  cbItemNumbers,
  commDevice,
  displayMonitor,
  fstLotteryProducts,
  lotteryFailureModes,
  partsBenchRepair,
  peripherals,
  powerSupplyItemNumbers,
  printers,
  quadBursterNumberItems,
  readerItemNumbers,
  solutions,
  status,
  terminals,
} from '../../../data/forms/depot/benchRepairDropdownOptions.js';
import { depotTimeWorked } from '../../../data/forms/depot/depotDropdownOptions.js';
import { hasSameContents } from '../../../utils/arrayUtils.js';
import { hasSameDate } from '../../../utils/dateUtils.js';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useAuth } from '../../../context/authContext.js';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

export default function EditWorklogModal({
  isOpen,
  handleClose,
  entry,
  handleRefresh,
  setIsSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
}) {
  const { user } = useAuth();
  let itemNumberOptions = '';
  switch (entry.product) {
    case 'Circuit Boards':
    case 'Printer Circuit Boards':
      itemNumberOptions = cbItemNumbers;
      break;
    case 'Readers':
      itemNumberOptions = readerItemNumbers;
      break;
    case 'Power Supplies':
      itemNumberOptions = powerSupplyItemNumbers;
      break;
    case 'Bill Acceptors':
      itemNumberOptions = billAcceptorNumberItems;
      break;
    case 'MINI/Quad Bursters':
      itemNumberOptions = quadBursterNumberItems;
      break;
    case 'Terminal':
      itemNumberOptions = terminals;
      break;
    case 'Printers':
      itemNumberOptions = printers;
      break;
    case 'Comm Device':
      itemNumberOptions = commDevice;
      break;
    case 'Display/Monitors':
      itemNumberOptions = displayMonitor;
      break;
    case 'Peripherals':
      itemNumberOptions = peripherals;
      break;
    default:
      break;
  }

  const [assetTag, setAssetTag] = useState(
    entry.assetTag ? entry.assetTag : ''
  );
  const [serialNumber, setSerialNumber] = useState(
    entry.serialNumber ? entry.serialNumber : ''
  );
  const [itemNumber, setItemNumber] = useState(
    entry.itemNumber ? entry.itemNumber : ''
  );
  const [statusVal, setStatusVal] = useState(entry.status ? entry.status : '');
  const [startDate, setStartDate] = useState(
    entry.startDate ? entry.startDate : null
  );
  const [completionDate, setCompletionDate] = useState(
    entry.completionDate ? entry.completionDate : null
  );
  const [partsUsed, setPartsUsed] = useState(
    entry.partsUsed ? entry.partsUsed : []
  );
  const [solution, setSolution] = useState(
    entry.solution ? entry.solution : []
  );
  const [failureMode, setFailureMode] = useState(
    entry.failureMode ? entry.failureMode : ''
  );
  const [totalWorkTime, setTotalWorkTime] = useState(
    entry.totalWorkTime ? entry.totalWorkTime : ''
  );
  const [comments, setComments] = useState(
    entry.comments ? entry.comments : ''
  );
  const [isRepeatReturn, setIsRepeatReturn] = useState(
    entry.isRepeatReturn ? entry.isRepeatReturn : ''
  );
  const [returnDate, setReturnDate] = useState(
    entry.returnDate ? entry.returnDate : null
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleIsRepeatReturnChange(e) {
    setIsRepeatReturn(e.target.value);
  }

  function handlePartsUsedChange(e, value) {
    setPartsUsed(typeof value === 'string' ? value.split(',') : value);
  }

  function handleSolutionChange(e) {
    setSolution(
      typeof e.target.value === 'string'
        ? e.target.value.split(',')
        : e.target.value
    );
  }

  const isFormValid = () => {
    return (
      (assetTag !== entry.assetTag && assetTag !== '') ||
      serialNumber !== entry.serialNumber ||
      itemNumber !== entry.itemNumber ||
      statusVal !== entry.status ||
      !hasSameDate(startDate, entry.startDate) ||
      !hasSameDate(completionDate, entry.completionDate) ||
      !hasSameContents(partsUsed, entry.partsUsed) ||
      !hasSameContents(solution, entry.solution) ||
      failureMode !== entry.failureMode ||
      (totalWorkTime !== entry.totalWorkTime && totalWorkTime !== '') ||
      (comments !== entry.comments && comments !== '') ||
      (isRepeatReturn !== entry.isRepeatReturn && isRepeatReturn !== '') ||
      !hasSameDate(returnDate, entry.returnDate) ||
      (comments !== entry.comments && comments !== '')
    );
  };

  async function submit() {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/rrf/lot/repair/worklog/submit`,
        data: {
          startDate: startDate !== entry.startDate ? startDate : null,
          completionDate:
            completionDate !== entry.completionDate ? completionDate : null,
          totalWorkTime:
            totalWorkTime !== entry.totalWorkTime ? totalWorkTime : null,
          comments: comments !== entry.comments ? comments : null,
          logId: entry.logId,
          assetTag: assetTag !== entry.assetTag ? assetTag : null,
          serialNumber:
            serialNumber !== entry.serialNumber ? serialNumber : null,
          itemNumber: itemNumber !== entry.itemNumber ? itemNumber : null,
          status: statusVal !== entry.status ? statusVal : null,
          partsUsed: partsUsed !== entry.partsUsed ? partsUsed : null,
          solution: solution !== entry.solution ? solution : null,
          failureMode: failureMode !== entry.failureMode ? failureMode : null,
          isRepeatReturn:
            isRepeatReturn !== entry.isRepeatReturn ? isRepeatReturn : null,
          returnDate: returnDate !== entry.returnDate ? returnDate : null,
          itemId: entry.id,
          repairOrder: entry.repairOrder,
          userId: user.userId,
          entryId: entry.entryId,
          displayName: user.displayName,
        },
      });
      setSnackbarMessage('Worklog edited successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClose();
      handleRefresh();
    } catch (error) {
      console.error('Error submitting worklog edit:', error);
      setSnackbarMessage(
        'There was an error submitting the worklog edit. Please try again. If the problem persists, contact support.'
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Grid
        container
        spacing={2}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', sm: '90vw', md: 800 },
          bgcolor: 'var(--dust-white)',
          p: 4,
          overflowY: 'auto',
          maxHeight: { xs: '90vh', md: '100%' },
        }}
      >
        <Grid size={12}>
          <Typography
            id='modal-modal-title'
            sx={{ fontFamily: 'BrightstarSans-Regular', fontSize: '25px' }}
          >
            {'Edit Entry'}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            disabled={true}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Jurisdiction'}</InputLabel>
            <Select label={'Jurisdiction'} value={entry.jurisdiction}>
              {jids.map(value => {
                return <MenuItem value={value}>{value}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <TextField
            label='Repair Order Number'
            value={entry.repairOrder}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            disabled={true}
          />
          <FormControl
            disabled={true}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>Product</InputLabel>
            <Select
              label={'Product'}
              value={
                entry.product === 'Printer Circuit Boards'
                  ? 'Circuit Boards'
                  : entry.product
              }
            >
              {fstLotteryProducts.map(mode => {
                return <MenuItem value={mode}>{mode}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label='Start Date'
              value={startDate ? dayjs(startDate) : null}
              onChange={value => setStartDate(value)}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </LocalizationProvider>
          <Autocomplete
            multiple
            value={partsUsed}
            options={partsBenchRepair}
            disableCloseOnSelect
            getOptionLabel={option => option}
            onChange={handlePartsUsedChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            renderInput={params => (
              <TextField {...params} name='partsUsed' label='Parts Used' />
            )}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              );
            }}
          />
          <FormControl
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>Failure Mode</InputLabel>
            <Select
              label={'Failure Mode'}
              value={failureMode}
              onChange={event => setFailureMode(event.target.value)}
            >
              {lotteryFailureModes.map(mode => {
                return <MenuItem value={mode}>{mode}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <TextField
            label='Comments'
            value={comments}
            onChange={event => setComments(event.target.value)}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label='Asset Tag'
            value={assetTag}
            onChange={event => setAssetTag(event.target.value)}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label='Serial Number'
            value={serialNumber}
            onChange={event => setSerialNumber(event.target.value)}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          {entry.itemNumber && (
            <FormControl
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            >
              <InputLabel>Item Number</InputLabel>
              <Select
                label={'Item Number'}
                value={itemNumber}
                onChange={event => setItemNumber(event.target.value)}
              >
                {itemNumberOptions.map(mode => {
                  return <MenuItem value={mode}>{mode}</MenuItem>;
                })}
              </Select>
            </FormControl>
          )}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Tooltip
              title={
                startDate ? '' : 'Please select Start Date to enable this field'
              }
              arrow
            >
              <span>
                <DatePicker
                  label='Completion Date'
                  value={completionDate ? dayjs(completionDate) : null}
                  minDate={startDate ? dayjs(startDate) : null}
                  disabled={startDate ? false : true}
                  onChange={value => setCompletionDate(value)}
                  sx={{
                    backgroundColor: '#ffffff',
                    marginBottom: '15px',
                    width: '100%',
                  }}
                />
              </span>
            </Tooltip>
          </LocalizationProvider>
          <FormControl
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>Status</InputLabel>
            <Select
              label={'Status'}
              value={statusVal}
              onChange={event => setStatusVal(event.target.value)}
            >
              {status.map(value => {
                return <MenuItem value={value}>{value}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <FormControl
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>Solution</InputLabel>
            <Select
              value={solution}
              onChange={handleSolutionChange}
              multiple
              input={
                <OutlinedInput id='select-multiple-chip' label='Solution' />
              }
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(value => (
                    <Chip label={value} />
                  ))}
                </Box>
              )}
            >
              {solutions.map(solutionVal => (
                <MenuItem value={solutionVal}>
                  <Checkbox checked={solution.includes(solutionVal)} />
                  <ListItemText primary={solutionVal} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Total Work Time (minutes)'}</InputLabel>
            <Select
              label={'Total Work Time (minutes)'}
              value={totalWorkTime}
              onChange={event => setTotalWorkTime(event.target.value)}
            >
              {depotTimeWorked.map(option => {
                return <MenuItem value={option.value}>{option.label}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <FormControl
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Repeat Return'}</InputLabel>
            <Select
              label={'Repeat Return'}
              value={isRepeatReturn}
              onChange={handleIsRepeatReturnChange}
            >
              {yesNoOptions.map(value => {
                return <MenuItem value={value}>{value}</MenuItem>;
              })}
            </Select>
          </FormControl>
          {isRepeatReturn === 'Yes' && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='Return Date'
                value={returnDate ? dayjs(returnDate) : null}
                onChange={value => setReturnDate(value)}
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              />
            </LocalizationProvider>
          )}
        </Grid>
        <Grid
          size={12}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button
            variant='contained'
            sx={{ marginRight: '10px', backgroundColor: 'var(--arcturus-red)' }}
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            disabled={!isFormValid() || isSubmitting}
            variant='contained'
            sx={{ backgroundColor: 'var(--vega-blue)' }}
            onClick={submit}
          >
            {'Submit'}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
}
